/**
 * KAN-773, KAN-790: лестница касаний (nudge_ladder) канала telegram_user.
 *
 * Шаг лестницы = задержка в часах от предыдущего касания + что делаем на шаге
 * (канал шага). Какие поля нужны шагу (рычаг, текст, срок), говорят признаки
 * канала needs_lever / needs_text / needs_due из channel_options: новый канал
 * бэка отрисуется и сохранится без правки фронта. Шаг можно привязать к этапу
 * воронки (пусто = любой этап). Списки рычагов, каналов шага и запрещённых
 * этапов приходят с бэка в поле схемы (options, channel_options,
 * blocked_stages), здесь они не хардкодятся.
 *
 * Чистые функции: нормализация значения из values канала (форма редактора с
 * умолчаниями), форма отправки на бэк, проверка лестницы перед сохранением
 * (повторяет правила telegram_bot/userbot/ladder_config.py) и подпись
 * задержки. Редактор - components/common/NudgeLadderEditor.
 */

export interface NudgeLadderStep {
  /** Задержка от предыдущего касания, часы (целое 1..720). */
  delay_h: number;
  /** Код рычага из options поля (нужен каналу с needs_lever). */
  lever: string;
  /** Канал шага из channel_options; нет значения - сообщение бота. */
  channel?: string;
  /** Codename этапа воронки; пусто - любой этап. */
  stage?: string;
  /** Текст шага (1..200 знаков), нужен каналу с needs_text. */
  text?: string;
  /** Срок исполнения, часы (целое 1..720), нужен каналу с needs_due. */
  due_h?: number;
}

/** Шаг в форме бэка: только нужные ключи (см. serializeNudgeLadder). */
export interface NudgeLadderWireStep {
  delay_h: number;
  lever?: string;
  channel?: string;
  stage?: string;
  text?: string;
  due_h?: number;
}

export interface NudgeLeverOption {
  value: string;
  label: string;
}

/** Канал шага из channel_options; признаков нет у бэка до их появления. */
export interface NudgeChannelOption extends NudgeLeverOption {
  needs_lever?: boolean;
  needs_text?: boolean;
  needs_due?: boolean;
}

/** Какие поля нужны шагу на выбранном канале. */
export interface NudgeChannelNeeds {
  lever: boolean;
  text: boolean;
  due: boolean;
}

/** Данные поля схемы и воронки, нужные редактору и проверке лестницы. */
export interface NudgeLadderContext {
  options: ReadonlyArray<NudgeLeverOption>;
  /** Каналы шага; пусто - бэк до KAN-790, редактор без новых колонок. */
  channelOptions: ReadonlyArray<NudgeChannelOption>;
  /** Codename этапов, к которым шаг привязать нельзя. */
  blockedStages: ReadonlyArray<string>;
  /** Этапы воронки для селектора (уже без запрещённых). */
  stageOptions: ReadonlyArray<NudgeLeverOption>;
}

export type HoursUnit = 'days' | 'hours';

/**
 * Единственное место с именами каналов шага: канал шага без ключа channel
 * (лестницы до KAN-790) и умолчания признаков для бэка, который отдаёт в
 * channel_options только value и label. Канал с признаками в схеме сюда не
 * заглядывает; новые каналы сюда не добавлять.
 */
export const NUDGE_CHANNEL_DEFAULT = 'telegram';
const LEGACY_CHANNEL_NEEDS: Readonly<Record<string, NudgeChannelNeeds>> = {
  [NUDGE_CHANNEL_DEFAULT]: { lever: true, text: false, due: false },
  manager_task: { lever: false, text: true, due: true },
};

/** Потолок одной лестницы (общей и лестницы этапа вместе с общими шагами). */
export const NUDGE_LADDER_MAX_STEPS = 5;
/** Потолок всех шагов в настройках. */
export const NUDGE_LADDER_MAX_TOTAL = 10;
export const NUDGE_DELAY_MIN_H = 1;
export const NUDGE_DELAY_MAX_H = 720;
export const NUDGE_TASK_TEXT_MAX = 200;
export const NUDGE_DUE_DEFAULT_H = 24;

export const NUDGE_LADDER_ORDER_ERROR = 'Задержки касаний должны возрастать';
export const NUDGE_LADDER_EMPTY_ERROR = 'Добавьте хотя бы одно касание';
export const NUDGE_LADDER_TOO_MANY_ERROR = `Не больше ${NUDGE_LADDER_MAX_STEPS} касаний`;
export const NUDGE_LADDER_TOTAL_ERROR = `Не больше ${NUDGE_LADDER_MAX_TOTAL} касаний всего`;

/** Целое в границах 1..720 или null, если это не число. */
export function clampDelayHours(raw: unknown): number | null {
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (raw === '' || raw == null || !Number.isFinite(num)) return null;
  return Math.min(NUDGE_DELAY_MAX_H, Math.max(NUDGE_DELAY_MIN_H, Math.round(num)));
}

/** Единица показа часов: дни, если делится на 24. */
export function unitForHours(hours: number): HoursUnit {
  return hours % 24 === 0 ? 'days' : 'hours';
}

/** Часы 1..720 из набранного числа в выбранной единице; пусто, мусор, не больше нуля: null. */
export function hoursFromInput(text: string, unit: HoursUnit): number | null {
  const num = Number(text);
  if (text.trim() === '' || !Number.isFinite(num) || num <= 0) return null;
  return clampDelayHours(unit === 'days' ? num * 24 : num);
}

/** Канал шага (нет значения - сообщение бота). */
export function stepChannel(step: NudgeLadderStep): string {
  return step.channel || NUDGE_CHANNEL_DEFAULT;
}

/** Этап шага ('' - любой этап). */
export function stepStage(step: NudgeLadderStep): string {
  return step.stage ?? '';
}

/**
 * Поля, нужные каналу. Признаки берутся из channel_options; канал без
 * признаков в схеме (старый бэк) или вовсе без записи читается по таблице
 * умолчаний, неизвестный ей канал ведёт себя как канал по умолчанию.
 */
export function channelNeeds(
  channel: string,
  channelOptions: ReadonlyArray<NudgeChannelOption> = []
): NudgeChannelNeeds {
  const option = channelOptions.find((o) => o.value === channel);
  const flags = option ? [option.needs_lever, option.needs_text, option.needs_due] : [];
  if (flags.some((flag) => typeof flag === 'boolean')) {
    return { lever: flags[0] === true, text: flags[1] === true, due: flags[2] === true };
  }
  return LEGACY_CHANNEL_NEEDS[channel] ?? LEGACY_CHANNEL_NEEDS[NUDGE_CHANNEL_DEFAULT];
}

/** Поля, нужные шагу на его канале. */
export function stepNeeds(
  step: NudgeLadderStep,
  channelOptions: ReadonlyArray<NudgeChannelOption> = []
): NudgeChannelNeeds {
  return channelNeeds(stepChannel(step), channelOptions);
}

/**
 * Список шагов из массива объектов или JSON-строки; иное - null.
 * Мусорные элементы отбрасываются, lever не из options заменяется первым
 * option (если options известны), delay_h приводится к целому 1..720.
 * KAN-790: шагу до этой задачи ({delay_h, lever}) подставляются умолчания:
 * канал "сообщение бота", любой этап, пустой текст, срок 24 часа. Текст не
 * обрезается (значение нормализуется на каждый рендер, пока пользователь печатает).
 */
export function normalizeNudgeLadder(
  raw: unknown,
  options: ReadonlyArray<NudgeLeverOption>
): NudgeLadderStep[] | null {
  let items: unknown = raw;
  if (typeof raw === 'string') {
    try {
      items = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(items)) return null;

  const known = new Set(options.map((o) => o.value));
  const fallbackLever = options[0]?.value;
  const steps: NudgeLadderStep[] = [];
  for (const item of items) {
    if (item === null || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const delay = clampDelayHours(record.delay_h);
    if (delay === null) continue;
    const rawLever = typeof record.lever === 'string' ? record.lever : '';
    let lever = rawLever;
    if (known.size > 0 && !known.has(rawLever)) lever = fallbackLever as string;
    steps.push({
      delay_h: delay,
      lever,
      channel: typeof record.channel === 'string' && record.channel ? record.channel : NUDGE_CHANNEL_DEFAULT,
      stage: typeof record.stage === 'string' ? record.stage.trim() : '',
      text: typeof record.text === 'string' ? record.text : '',
      due_h: clampDelayHours(record.due_h) ?? NUDGE_DUE_DEFAULT_H,
    });
  }
  return steps;
}

/**
 * KAN-790: шаги в форме бэка. Шаг канала по умолчанию без этапа уходит как
 * раньше ({delay_h, lever}); channel и stage добавляются только при отличии
 * от умолчаний. Рычаг, text (без пробелов по краям) и due_h отправляются
 * только когда они нужны каналу шага (признаки из channelOptions).
 */
export function serializeNudgeLadder(
  steps: ReadonlyArray<NudgeLadderStep>,
  channelOptions: ReadonlyArray<NudgeChannelOption> = []
): NudgeLadderWireStep[] {
  return steps.map((step) => {
    const needs = stepNeeds(step, channelOptions);
    const wire: NudgeLadderWireStep = { delay_h: step.delay_h };
    if (needs.lever) wire.lever = step.lever;
    const channel = stepChannel(step);
    if (channel !== NUDGE_CHANNEL_DEFAULT) wire.channel = channel;
    const stage = stepStage(step);
    if (stage) wire.stage = stage;
    if (needs.text) wire.text = (step.text ?? '').trim();
    if (needs.due) wire.due_h = step.due_h ?? NUDGE_DUE_DEFAULT_H;
    return wire;
  });
}

/** Этап воронки в объёме, нужном селектору шага. */
interface StageLike {
  codename: string;
  label: string;
  is_active?: boolean;
}

/**
 * Этапы для селектора шага: активные, без запрещённых, codename один раз
 * (общий список организации смешивает воронки, codename в них повторяется).
 */
export function nudgeStageOptions(
  stages: ReadonlyArray<StageLike> | undefined,
  blockedStages: ReadonlyArray<string>
): NudgeLeverOption[] {
  const skip = new Set(blockedStages);
  const result: NudgeLeverOption[] = [];
  for (const stage of stages ?? []) {
    if (stage.is_active === false || skip.has(stage.codename)) continue;
    skip.add(stage.codename);
    result.push({ value: stage.codename, label: stage.label });
  }
  return result;
}

/** Контекст лестницы из поля схемы канала и этапов воронки. */
export function nudgeLadderContext(
  field: {
    options?: ReadonlyArray<NudgeLeverOption>;
    channel_options?: ReadonlyArray<NudgeChannelOption>;
    blocked_stages?: ReadonlyArray<string>;
  },
  stages: ReadonlyArray<StageLike> | undefined
): NudgeLadderContext {
  const blockedStages = field.blocked_stages ?? [];
  return {
    options: field.options ?? [],
    channelOptions: field.channel_options ?? [],
    blockedStages,
    stageOptions: nudgeStageOptions(stages, blockedStages),
  };
}

/** Изъян одного шага без номера или null. */
function stepError(
  step: NudgeLadderStep,
  blocked: ReadonlySet<string>,
  channelOptions: ReadonlyArray<NudgeChannelOption>
): string | null {
  const stage = stepStage(step);
  if (stage && blocked.has(stage)) return `на этапе "${stage}" лестница не работает, выберите другой этап`;
  const needs = stepNeeds(step, channelOptions);
  if (needs.lever && !step.lever) return 'выберите рычаг';
  if (needs.text) {
    const text = (step.text ?? '').trim();
    if (!text) return 'укажите текст задачи';
    if (text.length > NUDGE_TASK_TEXT_MAX) return `текст задачи длиннее ${NUDGE_TASK_TEXT_MAX} знаков`;
  }
  if (needs.due) {
    const due = step.due_h ?? NUDGE_DUE_DEFAULT_H;
    if (!Number.isInteger(due) || due < NUDGE_DELAY_MIN_H || due > NUDGE_DELAY_MAX_H) {
      return `срок задачи от ${NUDGE_DELAY_MIN_H} часа до ${NUDGE_DELAY_MAX_H / 24} дней`;
    }
  }
  return null;
}

/** Правила лестницы этапа (общие шаги плюс шаги этапа): потолок и разные задержки. */
function stageLadderError(
  steps: ReadonlyArray<NudgeLadderStep>,
  stage: string,
  label: string
): string | null {
  const delays = steps.filter((s) => stepStage(s) === '' || stepStage(s) === stage).map((s) => s.delay_h);
  if (delays.length > NUDGE_LADDER_MAX_STEPS) {
    return stage
      ? `Этап "${label}": не больше ${NUDGE_LADDER_MAX_STEPS} касаний вместе с общими`
      : NUDGE_LADDER_TOO_MANY_ERROR;
  }
  if (new Set(delays).size !== delays.length) {
    return stage
      ? `Этап "${label}": задержки касаний не должны совпадать (с учётом общих)`
      : NUDGE_LADDER_ORDER_ERROR;
  }
  return null;
}

/**
 * Текст ошибки лестницы или null. Правила бэка (KAN-790): 1..10 шагов всего;
 * этап не из запрещённых; по признакам канала шага: рычаг выбран, текст
 * 1..200 знаков, срок 1..720 часов; общие шаги (без этапа) идут по строго возрастающей задержке;
 * лестница каждого этапа (общие шаги плюс шаги этапа) не длиннее 5 и без
 * одинаковых задержек.
 */
export function nudgeLadderError(
  steps: ReadonlyArray<NudgeLadderStep>,
  context: Partial<Pick<NudgeLadderContext, 'blockedStages' | 'stageOptions' | 'channelOptions'>> = {}
): string | null {
  if (steps.length === 0) return NUDGE_LADDER_EMPTY_ERROR;
  if (steps.length > NUDGE_LADDER_MAX_TOTAL) return NUDGE_LADDER_TOTAL_ERROR;

  const blocked = new Set(context.blockedStages ?? []);
  for (let i = 0; i < steps.length; i += 1) {
    const error = stepError(steps[i], blocked, context.channelOptions ?? []);
    if (error) return `Касание ${i + 1}: ${error}`;
  }

  const common = steps.filter((s) => stepStage(s) === '').map((s) => s.delay_h);
  for (let i = 1; i < common.length; i += 1) {
    if (common[i] <= common[i - 1]) return NUDGE_LADDER_ORDER_ERROR;
  }

  const labels = new Map((context.stageOptions ?? []).map((o) => [o.value, o.label]));
  const stages = ['', ...new Set(steps.map(stepStage).filter((stage) => stage !== ''))];
  for (const stage of stages) {
    const error = stageLadderError(steps, stage, labels.get(stage) ?? stage);
    if (error) return error;
  }
  return null;
}

/** Русская форма множественного числа: 1 день, 2 дня, 5 дней. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** "через 1 день" / "через 3 дня" / "через 12 часов" (дни, если делится на 24). */
export function formatDelay(hours: number): string {
  if (hours > 0 && hours % 24 === 0) {
    const days = hours / 24;
    return `через ${days} ${plural(days, 'день', 'дня', 'дней')}`;
  }
  return `через ${hours} ${plural(hours, 'час', 'часа', 'часов')}`;
}
